import { useT } from '@/store';
import { Modal } from '@/components/ui/Overlay';
import { SecretField } from '@/components/ui/SecretField';

/**
 * Shows newly-issued driver credentials — once.
 *
 * The backend returns these in the create/rotate/reset response and never
 * again; only the hash lives on. So this is a modal the admin has to
 * dismiss deliberately, with explicit copy buttons and a warning, rather
 * than a toast that could expire while they were looking at their phone.
 * Rotating a key immediately invalidates the old one, which is why the
 * warning says so plainly — a driver mid-delivery gets logged out.
 *
 * The username and password are shown alongside the API key because they
 * are what the driver actually types: the Driver App's only sign-in screen
 * takes a username and a password, and the key is what the app holds
 * afterwards. Creation used to return neither, and the driver stayed locked
 * out until a server restart minted them a password derived from their own
 * name. Whichever fields the response carries are the ones rendered, so the
 * same modal serves a create (key + login), a key rotation (key alone) and
 * a password reset (login alone).
 */
export function DriverKeyModal({ issued, onClose }) {
  const t = useT();

  return (
    <Modal
      open={Boolean(issued)}
      onClose={onClose}
      title={t('drivers.newKeyTitle')}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t('common.closeBtn')}
        </button>
      }
    >
      {issued ? (
        <div className="space-y-3">
          <p className="text-[12.5px]">{issued.name}</p>

          {issued.username ? (
            <SecretField label={t('drivers.loginUsername')} value={issued.username} />
          ) : null}

          {issued.password ? (
            <SecretField label={t('drivers.loginPassword')} value={issued.password} />
          ) : null}

          {issued.apiKey ? (
            <SecretField label={t('drivers.apiKeyLabel')} value={issued.apiKey} />
          ) : null}

          <p className="rounded-[10px] border border-amber/25 bg-amber/8 px-3 py-2 text-[11.5px] leading-snug text-amber">
            {t(issued.apiKey ? 'drivers.newKeyWarning' : 'drivers.newPasswordWarning')}
          </p>
        </div>
      ) : null}
    </Modal>
  );
}

